import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyleWide, FormRow, DetailField, fmtMoney, fmtQty } from './ProcurementShared';

const emptyLine = () => ({ item_type: 'description', description: '', quantity: 1, unit: '', unit_cost: 0, discount_pct: 0, tax_pct: 0 });

export default function ProcurementPurchaseOrders() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/procurement/purchase-orders?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/procurement/suppliers?per_page=200`, { headers: getAuthHeaders() });
      const json = await res.json();
      setSuppliers(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); }
  }, [companyId]);

  useEffect(() => { fetchList(); fetchSuppliers(); }, [fetchList, fetchSuppliers]);

  const viewPo = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-orders/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const doAction = async (id, action, extra = {}) => {
    if ((action === 'reject' || action === 'cancel') && !extra.reason) {
      const reason = window.prompt(`${action === 'reject' ? 'Rejection' : 'Cancellation'} reason (required):`);
      if (!reason) return;
      extra = { ...extra, reason };
    }
    const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-orders/${id}/${action}`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(extra),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Action failed'); return; }
    fetchList();
    if (viewing) viewPo(id);
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this purchase order?')) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-orders/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🧾 Purchase Orders</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Purchase Order</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search PO #/reference..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="partially_received">Partially Received</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['PO #', 'Supplier', 'Total', 'Receiving', 'Invoice', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No purchase orders</td></tr>}
              {list.map(po => (
                <tr key={po.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}><a href="#view" onClick={e => { e.preventDefault(); viewPo(po.id); }} style={{ color: '#3b82f6' }}>{po.po_number}</a></td>
                  <td style={tdStyle}>{po.supplier?.name || '—'}</td>
                  <td style={tdStyle}>{fmtMoney(po.total)}</td>
                  <td style={tdStyle}><Badge value={po.receiving_status} /></td>
                  <td style={tdStyle}><Badge value={po.supplier_invoice_status} /></td>
                  <td style={tdStyle}><Badge value={po.status} /></td>
                  <td style={tdStyle}>
                    {po.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(po)}>Edit</button>}
                    {po.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => removeItem(po.id)}>Delete</button>}
                    <button style={btnSmall} onClick={() => viewPo(po.id)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PoModal companyId={companyId} po={editing} suppliers={suppliers}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}
      {viewing && <PoDetail po={viewing} onClose={() => setViewing(null)} onAction={doAction} />}
    </div>
  );
}

function PoModal({ companyId, po, suppliers, onClose, onSaved }) {
  const isNew = !po.id;
  const [form, setForm] = useState({
    supplier_id: po.supplier_id || '', department_name: po.department_name || '',
    po_date: po.po_date ? po.po_date.substring(0, 10) : '', expected_delivery_date: po.expected_delivery_date ? po.expected_delivery_date.substring(0, 10) : '',
    purchase_type: po.purchase_type || 'products', payment_terms: po.payment_terms || '', notes: po.notes || '',
  });
  const [lines, setLines] = useState(po.lines && po.lines.length ? po.lines.map(l => ({ ...l })) : [emptyLine()]);
  const [saving, setSaving] = useState(false);

  const updateLine = (i, field, val) => {
    const next = [...lines];
    next[i] = { ...next[i], [field]: val };
    setLines(next);
  };
  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const lineTotal = (l) => {
    const sub = (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
    const disc = sub * (Number(l.discount_pct) || 0) / 100;
    const taxable = sub - disc;
    const tax = taxable * (Number(l.tax_pct) || 0) / 100;
    return taxable + tax;
  };
  const grandTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const save = async () => {
    if (!form.supplier_id) { alert('Supplier is required'); return; }
    const validLines = lines.filter(l => l.description && Number(l.quantity) > 0);
    if (validLines.length === 0) { alert('At least one line item is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/procurement/purchase-orders` : `${API}/companies/${companyId}/procurement/purchase-orders/${po.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lines: validLines }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Purchase Order' : `Edit ${po.po_number}`}</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <FormRow label="Supplier *">
              <select style={inputStyle} value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })}>
                <option value="">Select supplier...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormRow>
          </div>
          <div style={{ flex: 1 }}><FormRow label="Department"><input style={inputStyle} value={form.department_name} onChange={e => setForm({ ...form, department_name: e.target.value })} /></FormRow></div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="PO Date"><input type="date" style={inputStyle} value={form.po_date} onChange={e => setForm({ ...form, po_date: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Expected Delivery"><input type="date" style={inputStyle} value={form.expected_delivery_date} onChange={e => setForm({ ...form, expected_delivery_date: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}>
            <FormRow label="Purchase Type">
              <select style={inputStyle} value={form.purchase_type} onChange={e => setForm({ ...form, purchase_type: e.target.value })}>
                <option value="products">Products</option><option value="non_stock">Non-Stock</option>
                <option value="services">Services</option><option value="expenses">Expenses</option><option value="mixed">Mixed</option>
              </select>
            </FormRow>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Payment Terms"><input style={inputStyle} value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} placeholder="net_30" /></FormRow></div>
        </div>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>

        <h4 style={{ marginBottom: 8 }}>Line Items</h4>
        <table style={tableStyle}>
          <thead><tr>{['Description', 'Qty', 'Unit Cost', 'Disc %', 'Tax %', 'Line Total', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td style={tdStyle}><input style={inputStyle} value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 70 }}><input type="number" style={inputStyle} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 100 }}><input type="number" style={inputStyle} value={l.unit_cost} onChange={e => updateLine(i, 'unit_cost', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 70 }}><input type="number" style={inputStyle} value={l.discount_pct} onChange={e => updateLine(i, 'discount_pct', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 70 }}><input type="number" style={inputStyle} value={l.tax_pct} onChange={e => updateLine(i, 'tax_pct', e.target.value)} /></td>
                <td style={tdStyle}>{fmtMoney(lineTotal(l))}</td>
                <td style={tdStyle}><button style={btnSmall} onClick={() => removeLine(i)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button style={{ ...btnSecondary, marginTop: 8 }} onClick={addLine}>+ Add Line</button>

        <div style={{ textAlign: 'right', marginTop: 10, fontWeight: 700 }}>Total: {fmtMoney(grandTotal)}</div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function PoDetail({ po, onClose, onAction }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{po.po_number} <Badge value={po.status} /></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
          <DetailField label="Supplier" value={po.supplier?.name} />
          <DetailField label="PO Date" value={po.po_date ? new Date(po.po_date).toLocaleDateString() : '—'} />
          <DetailField label="Expected Delivery" value={po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : '—'} />
          <DetailField label="Total" value={fmtMoney(po.total)} />
          <DetailField label="Total Invoiced" value={fmtMoney(po.total_supplier_invoiced)} />
          <DetailField label="Remaining to Invoice" value={fmtMoney(po.remaining_to_invoice)} />
          <DetailField label="Receiving Status" value={<Badge value={po.receiving_status} />} />
          <DetailField label="Invoice Status" value={<Badge value={po.supplier_invoice_status} />} />
          <DetailField label="Payment Status" value={<Badge value={po.payment_status} />} />
        </div>

        <h4>Line Items</h4>
        <table style={tableStyle}>
          <thead><tr>{['Description', 'Qty', 'Unit Cost', 'Received', 'Invoiced', 'Line Total'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(po.lines || []).map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.description}</td>
                <td style={tdStyle}>{fmtQty(l.quantity)}</td>
                <td style={tdStyle}>{fmtMoney(l.unit_cost)}</td>
                <td style={tdStyle}>{fmtQty(l.received_qty)}</td>
                <td style={tdStyle}>{fmtQty(l.invoiced_qty)}</td>
                <td style={tdStyle}>{fmtMoney(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {(po.payment_schedules || []).length > 0 && (
          <>
            <h4>Payment Schedule</h4>
            <table style={tableStyle}>
              <thead><tr>{['#', 'Amount', 'Due Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {po.payment_schedules.map(s => (
                  <tr key={s.id}><td style={tdStyle}>{s.installment_number}</td><td style={tdStyle}>{fmtMoney(s.amount)}</td><td style={tdStyle}>{s.due_date ? new Date(s.due_date).toLocaleDateString() : '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {(po.approvals || []).length > 0 && (
          <>
            <h4>Approvals</h4>
            <table style={tableStyle}>
              <thead><tr>{['Level', 'Decision', 'Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {po.approvals.map(a => (
                  <tr key={a.id}><td style={tdStyle}>{a.level}</td><td style={tdStyle}><Badge value={a.decision} /></td><td style={tdStyle}>{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          {po.status === 'draft' && po.approval_status !== 'pending_approval' && <button style={btnPrimary} onClick={() => onAction(po.id, 'confirm')}>Confirm</button>}
          {po.approval_status === 'pending_approval' && <button style={btnPrimary} onClick={() => onAction(po.id, 'approve')}>Approve</button>}
          {po.approval_status === 'pending_approval' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => onAction(po.id, 'reject')}>Reject</button>}
          {po.status !== 'cancelled' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => onAction(po.id, 'cancel')}>Cancel</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
